import { BulkValidatedRow } from "@/redux/apis/clientBooking/types";

export type ImportSelection = {
  /** Row indexes ticked for import. */
  selected: Set<number>;
  /** Ticked rows the server built a payload for — what actually gets booked, and the confirm count. */
  importableCount: number;
  /** Ticked rows the server rejected. Anything above zero holds the import button shut. */
  blockingCount: number;
};

/**
 * Works out what the Import button should do, given every validated row and the rows the user has
 * unticked (DEV-1215).
 *
 * **Selection is stored as exclusions, not inclusions.** Someone who uploaded fifty bookings wants
 * fifty bookings, so a row counts as ticked the moment it validates — no effect has to run to tick
 * it, which also means rows streaming in a chunk at a time never flash unticked first.
 *
 * **A ticked-but-invalid row blocks the whole import.** The alternative — quietly skipping it — books
 * 47 of 50 and leaves the user to spot the shortfall themselves. Blocking forces a decision: fix the
 * row, or untick it to say the skip was intended.
 */
/**
 * May the Import button be pressed?
 *
 * **`isValidating` is the one that matters and the one that was missing.** Every other count here is
 * derived from rows that have come BACK, so on a hundred-row file the first clean chunk of ten made
 * the button live while ninety were still unchecked. Pressing it queued ten bookings and silently
 * dropped the rest — and the confirmation said "import 10 bookings", which reads as correct unless
 * you happened to notice the number was still climbing.
 *
 * Kept next to `resolveImportSelection` because it reads that function's output, and because a gate
 * this easy to get wrong deserves somewhere it can be tested without standing up the wizard.
 */
export const canImport = ({
  isValidating,
  importableCount,
  blockingCount,
  isImporting,
}: {
  isValidating: boolean;
  importableCount: number;
  blockingCount: number;
  isImporting: boolean;
}): boolean => !isValidating && !isImporting && importableCount > 0 && blockingCount === 0;

export const resolveImportSelection = (validated: BulkValidatedRow[], unticked: Set<number>): ImportSelection => {
  const selectedRows = validated.filter((row) => !unticked.has(row.row_index));

  return {
    selected: new Set(selectedRows.map((row) => row.row_index)),
    importableCount: selectedRows.filter((row) => row.payload).length,
    blockingCount: selectedRows.filter((row) => row.status === "invalid").length,
  };
};
